import { scheduleClipFades } from './clipEdits';
/**
 * ExportRenderer.js — Section 5.3
 *
 * Renders the full arrangement to MP3 using OfflineAudioContext + lamejs.
 *
 * 6-step export flow:
 * 1. Create OfflineAudioContext (44100 Hz, 2 channels, total duration in samples)
 * 2. Rebuild the playback graph in the offline context
 * 3. offlineContext.startRendering() → AudioBuffer
 * 4. Convert AudioBuffer (Float32Array) → Int16Array (PCM)
 * 5. Encode PCM → MP3 via lamejs
 * 6. Return MP3 Blob
 */

import { secondsPerBar } from './AudioEngine';
import { getCached } from './BufferCache';
import { createMp3Encoder } from './lamejsWrapper';
import { clipPlaybackWindow } from './clipWindow';
import { ensureKnockBuffers } from './knockRender';
import { ensurePatternBuffers } from './patternRender';
import { playableBuffer } from './stretchedBuffers';
import { buildMixGraph, dottedEighthSeconds } from './mixGraph';
import { audioEligibleTracks } from './classAudio';

const SAMPLE_RATE = 44100;
const NUM_CHANNELS = 2;
const MP3_BITRATE = 128;

export function exportTailSeconds(tracks, bpm) {
  const audible = (tracks || []).filter((track) => !track.muted);
  const reverbTail = audible.some((track) => Number(track.effects?.reverb) > 0) ? 2.4 : 0;
  const echoTail = audible.some((track) => Number(track.effects?.echo ?? track.effects?.delay) > 0) ? 6 * dottedEighthSeconds(bpm) : 0;
  return Math.min(4, Math.max(reverbTail, echoTail));
}

/**
 * Render the arrangement to an MP3 Blob.
 *
 * @param {Array} tracks - Track objects with clips
 * @param {number} bpm - Beats per minute
 * @param {number} totalBars - Total bars
 * @param {Function} onProgress - (percent: 0-100) => void
 * @returns {Promise<Blob>} - MP3 Blob
 */
export async function renderToMp3(tracks, bpm, totalBars, onProgress) {
  onProgress?.(0);
  const eligibleTracks = audioEligibleTracks(tracks);
  await ensureKnockBuffers(eligibleTracks, bpm);
  await ensurePatternBuffers(eligibleTracks, bpm).catch((error) => console.warn('ExportRenderer: pattern render failed:', error.message));

  const spb = secondsPerBar(bpm);
  const songDuration = totalBars * spb;
  const tail = exportTailSeconds(tracks, bpm);
  const totalDuration = songDuration + tail;
  const totalSamples = Math.ceil(totalDuration * SAMPLE_RATE);

  // Step 1: Create OfflineAudioContext
  const offlineCtx = new OfflineAudioContext(NUM_CHANNELS, totalSamples, SAMPLE_RATE);

  const graph = buildMixGraph(offlineCtx, { tracks: eligibleTracks, bpm, destination: offlineCtx.destination });
  if (tail > 0) {
    const fadeAt = Math.max(songDuration, totalDuration - 0.3);
    graph.masterInput.gain.setValueAtTime(1, fadeAt);
    graph.masterInput.gain.linearRampToValueAtTime(0, totalDuration);
  }

  onProgress?.(10);

  // Step 2: Rebuild playback graph in offline context
  const anySoloed = eligibleTracks.some((t) => t.solo);

  for (const track of eligibleTracks) {
    let shouldPlay;
    if (anySoloed) {
      shouldPlay = track.solo && !track.muted;
    } else {
      shouldPlay = !track.muted;
    }

    if (!shouldPlay) continue;

    const trackInput = graph.trackInput(track.id);
    if (!trackInput) continue;

    // Schedule each clip
    for (const clip of track.clips) {
      const buffer = playableBuffer(clip, bpm, track);
      if (!buffer) {
        console.warn(`ExportRenderer: no buffer for loop ${clip.loop_id}, skipping`);
        continue;
      }

      const window = clipPlaybackWindow(clip, bpm, 0);
      if (!window) continue;

      const source = offlineCtx.createBufferSource();
      source.buffer = buffer;
      const clipGain = offlineCtx.createGain();
      source.connect(clipGain);
      clipGain.connect(trackInput);
      scheduleClipFades(clipGain.gain, clip, window, window.when, bpm);
      source.start(window.when, window.bufferOffset, window.duration);
    }
  }

  onProgress?.(20);

  // Step 3: Render
  const renderedBuffer = await offlineCtx.startRendering();
  graph.dispose();

  onProgress?.(50);

  // Step 4: Convert Float32Array → Int16Array
  const leftChannel = renderedBuffer.getChannelData(0);
  const rightChannel = renderedBuffer.numberOfChannels > 1
    ? renderedBuffer.getChannelData(1)
    : leftChannel;

  const leftInt16 = floatTo16Bit(leftChannel);
  const rightInt16 = floatTo16Bit(rightChannel);

  onProgress?.(60);

  // Step 5: Encode to MP3 via lamejs
  const mp3Blob = await encodeMp3(leftInt16, rightInt16, SAMPLE_RATE, onProgress);

  onProgress?.(100);

  return mp3Blob;
}

/**
 * Convert Float32Array audio data to Int16Array.
 */
function floatTo16Bit(float32Array) {
  const int16 = new Int16Array(float32Array.length);
  for (let i = 0; i < float32Array.length; i++) {
    const s = Math.max(-1, Math.min(1, float32Array[i]));
    int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return int16;
}

/**
 * Encode Int16Array stereo audio to MP3 Blob using lamejs.
 */
async function encodeMp3(leftChannel, rightChannel, sampleRate, onProgress) {
  const encoder = await createMp3Encoder(NUM_CHANNELS, sampleRate, MP3_BITRATE);
  const mp3Data = [];

  // Encode in chunks of 1152 samples (standard MP3 frame size)
  const CHUNK_SIZE = 1152;
  const totalChunks = Math.ceil(leftChannel.length / CHUNK_SIZE);

  for (let i = 0; i < leftChannel.length; i += CHUNK_SIZE) {
    const leftChunk = leftChannel.subarray(i, i + CHUNK_SIZE);
    const rightChunk = rightChannel.subarray(i, i + CHUNK_SIZE);

    const mp3buf = encoder.encodeBuffer(leftChunk, rightChunk);
    if (mp3buf.length > 0) {
      mp3Data.push(mp3buf);
    }

    // Progress update (60–95% range during encoding)
    if (onProgress && i % (CHUNK_SIZE * 100) === 0) {
      const chunkIndex = i / CHUNK_SIZE;
      const pct = 60 + Math.floor((chunkIndex / totalChunks) * 35);
      onProgress(Math.min(pct, 95));
    }
  }

  // Flush remaining
  const mp3End = encoder.flush();
  if (mp3End.length > 0) {
    mp3Data.push(mp3End);
  }

  // Step 6: Create Blob
  return new Blob(mp3Data, { type: 'audio/mp3' });
}

/**
 * Get estimated export duration for UI display.
 */
export function getEstimatedDuration(totalBars, bpm) {
  const spb = secondsPerBar(bpm);
  return totalBars * spb;
}
